import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaBell } from "react-icons/fa";
import { Portal } from "react-portal";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  useDropdownContext,
} from "components/molecules/Dropdown";
import { NotificationDetailModal } from "components/organisms/NotificationDetailModal";
import { RootState } from "store";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { Loader } from "components/atoms/Loader";
import { Center } from "components/atoms/Center";
import { Error } from "components/atoms/Error";
import { NavigationIconButton } from "../NavigationIconButton";
import { NotificationCardView } from "components/molecules/NotificationCard";
import { INotification } from "models/notification/notification.model";
import { recordNotificationDetailView } from "services/api/notifications";
import {
  openNotificationDetail,
  openNotificationsThunk,
} from "store/notificationsSlice/index.slice";

const NotificationCard = ({
  notification,
}: {
  notification: INotification;
}) => {
  const dispatch = useDispatch();
  const { onClose } = useDropdownContext();
  const hasDetail =
    !!notification.detailBody || !!notification.detailMedia?.url;

  return (
    <NotificationCardView
      category={notification.category}
      carrierName={notification.carrierName}
      title={notification.title}
      body={notification.body}
      linkUrl={notification.linkUrl}
      linkLabel={notification.linkLabel}
      createdDate={notification.createdDate}
      read={notification.read}
      hasDetail={hasDetail}
      onViewDetails={() => {
        // Record the click-through (fire-and-forget; analytics only).
        void recordNotificationDetailView(notification.id);
        onClose();
        dispatch(openNotificationDetail(notification.id));
      }}
    />
  );
};

// Mounts fresh each time the flyout opens, so it refetches on open.
const NotificationsFlyoutContent = () => {
  const dispatch = useDispatch();
  const { notifications, loadingStatus } = useSelector(
    (state: RootState) => state.notificationsSlice
  );

  useEffect(() => {
    dispatch(openNotificationsThunk());
  }, [dispatch]);

  const isInitialLoading =
    loadingStatus === ApiLoadingStatus.LOADING && notifications.length === 0;

  return (
    <div className="flex flex-col" style={{ maxHeight: 460 }}>
      <div className="flex items-center flex-shrink-0 px-4 py-3 border-b border-gray6">
        <span className="text-sm font-semibold">Notifications</span>
      </div>

      <div className="flex-1 overflow-auto">
        {isInitialLoading ? (
          <div className="py-8">
            <Center>
              <Loader />
            </Center>
          </div>
        ) : loadingStatus === ApiLoadingStatus.FAILED ? (
          <div className="py-8">
            <Error />
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-10 text-sm text-center text-gray10">
            You&apos;re all caught up.
          </div>
        ) : (
          <div className="divide-y divide-gray6">
            {notifications.map((notification) => (
              <NotificationCard
                key={notification.id}
                notification={notification}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const NotificationBell = () => {
  const { unreadCount } = useSelector(
    (state: RootState) => state.notificationsSlice
  );

  return (
    <>
      <Dropdown mode="onClick" placement="bottom-end" offset={[0, 8]}>
        <DropdownToggle>
          <div className="relative">
            <NavigationIconButton tooltip="Notifications" icon={FaBell} />
            {unreadCount > 0 && (
              <span className="absolute flex items-center justify-center w-4 h-4 text-xs font-semibold leading-none text-white rounded-full pointer-events-none -top-1 -right-1 bg-red9">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </div>
        </DropdownToggle>
        <Portal>
          <DropdownMenu width={400}>
            <NotificationsFlyoutContent />
          </DropdownMenu>
        </Portal>
      </Dropdown>
      {/* Outside the Dropdown so it survives the flyout closing. */}
      <NotificationDetailModal />
    </>
  );
};
